import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, ActivityIndicator, Modal } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { paymentRequestService, PaymentRequest } from '../services/paymentRequestService';
import PaymentMethodBrandIcon from '../components/PaymentMethodBrandIcon';

interface CustomerPaymentScreenProps {
  paymentRequestId: string;
  onPaymentComplete: (success: boolean) => void;
  onBack: () => void;
}

const CustomerPaymentScreen: React.FC<CustomerPaymentScreenProps> = ({
  paymentRequestId,
  onPaymentComplete,
  onBack,
}) => {
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    name: '',
    email: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPaymentRequest();
  }, [paymentRequestId]);

  const loadPaymentRequest = async () => {
    try {
      const request = await paymentRequestService.getPaymentRequest(paymentRequestId);
      if (request) {
        setPaymentRequest(request);
        
        // Check if expired
        if (new Date() > new Date(request.expiresAt)) {
          Alert.alert('Payment Expired', 'This payment request has expired. Please contact the business for a new payment link.');
          onBack();
          return;
        }
      } else {
        Alert.alert('Error', 'Payment request not found');
        onBack();
      }
    } catch (error) {
      console.error('Error loading payment request:', error);
      Alert.alert('Error', 'Failed to load payment request');
      onBack();
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodSelect = (methodId: string) => {
    setSelectedPaymentMethod(methodId);
    
    // For digital wallets, process immediately
    if (['apple_pay', 'google_pay', 'venmo', 'cash_app', 'zelle'].includes(methodId)) {
      handleProcessPayment(methodId);
    } else {
      // For cards, show payment form
      setShowPaymentModal(true);
    }
  };

  const handleProcessPayment = async (methodId?: string) => {
    if (!paymentRequest) return;

    const method = methodId || selectedPaymentMethod;
    if (!method) return;

    setProcessing(true);

    try {
      const success = await paymentRequestService.processPayment(
        paymentRequest.id,
        method,
        methodId ? undefined : paymentData
      );

      if (success) {
        Alert.alert(
          'Payment Successful!',
          `Your payment of $${paymentRequest.amount} has been processed successfully.`,
          [
            {
              text: 'OK',
              onPress: () => {
                onPaymentComplete(true);
              }
            }
          ]
        );
      } else {
        Alert.alert('Payment Failed', 'Please try again or contact support.');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      Alert.alert('Payment Error', 'An error occurred while processing your payment.');
    } finally {
      setProcessing(false);
      setShowPaymentModal(false);
    }
  };

  const formatCardNumber = (text: string) => {
    // Remove all non-digits
    const cleaned = text.replace(/\D/g, '');
    // Add spaces every 4 digits
    const formatted = cleaned.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted.slice(0, 19); // Max 16 digits + 3 spaces
  };

  const formatExpiryDate = (text: string) => {
    // Remove all non-digits
    const cleaned = text.replace(/\D/g, '');
    // Add slash after 2 digits
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  const getPaymentMethodName = (methodId: string) => {
    const method = paymentRequest?.paymentMethods.find(m => m.id === methodId);
    return method?.name || 'Unknown';
  };

  const getTimeRemaining = () => {
    if (!paymentRequest) return '';
    
    const now = new Date();
    const expires = new Date(paymentRequest.expiresAt);
    const diff = expires.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>Loading payment request...</Text>
      </View>
    );
  }

  if (!paymentRequest) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>Payment request not found</Text>
        <TouchableOpacity style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Payment Request</Text>
      </View>

      {/* Payment Request Details */}
      <View style={styles.paymentDetailsCard}>
        <View style={styles.businessInfo}>
          <Text style={styles.businessName}>Elite Hair Studio</Text>
          <Text style={styles.businessCategory}>Hair Salon</Text>
        </View>

        <View style={styles.serviceInfo}>
          <Text style={styles.serviceName}>{paymentRequest.serviceName}</Text>
          <Text style={styles.serviceDescription}>{paymentRequest.description}</Text>
        </View>

        <View style={styles.amountSection}>
          <Text style={styles.amountLabel}>Amount</Text>
          <Text style={styles.amountValue}>${paymentRequest.amount}</Text>
        </View>

        <View style={styles.expirationInfo}>
          <Text style={styles.expirationLabel}>⏰ {getTimeRemaining()}</Text>
        </View>
      </View>

      {/* Payment Methods */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Choose Payment Method</Text>
        <View style={styles.paymentMethodsList}>
          {paymentRequest.paymentMethods.map(method => (
            <TouchableOpacity
              key={method.id}
              style={[
                styles.paymentMethodCard,
                selectedPaymentMethod === method.id && styles.selectedPaymentMethod
              ]}
              onPress={() => handlePaymentMethodSelect(method.id)}
            >
              <View style={styles.paymentMethodIconWrap}>
                <PaymentMethodBrandIcon methodId={method.id} size={36} />
              </View>
              <Text style={styles.paymentMethodName}>{method.name}</Text>
              {method.type === 'digital_wallet' && (
                <Text style={styles.paymentMethodBadge}>Quick Pay</Text>
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Security Info */}
      <View style={styles.securitySection}>
        <Text style={styles.securityTitle}>🔒 Secure Payment</Text>
        <Text style={styles.securityText}>
          Your payment is processed securely through Stripe. We never store your payment information.
        </Text>
      </View>

      {/* Processing Indicator */}
      {processing && (
        <View style={styles.processingOverlay}>
          <View style={styles.processingCard}>
            <ActivityIndicator size="large" color="#007AFF" />
            <Text style={styles.processingText}>Processing Payment...</Text>
            <Text style={styles.processingSubtext}>Please don't close this screen</Text>
          </View>
        </View>
      )}

      {/* Payment Form Modal */}
      <Modal
        visible={showPaymentModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Payment Details</Text>
              <TouchableOpacity onPress={() => setShowPaymentModal(false)}>
                <Text style={styles.modalCloseButton}>✕</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.paymentForm}>
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Card Number</Text>
                <AppTextInput
                  style={styles.input}
                  value={paymentData.cardNumber}
                  onChangeText={(text) => setPaymentData({...paymentData, cardNumber: formatCardNumber(text)})}
                  placeholder="1234 5678 9012 3456"
                  keyboardType="numeric"
                  maxLength={19}
                />
              </View>

              <View style={styles.row}>
                <View style={[styles.inputGroup, styles.halfWidth]}>
                  <Text style={styles.inputLabel}>Expiry Date</Text>
                  <AppTextInput
                    style={styles.input}
                    value={paymentData.expiryDate}
                    onChangeText={(text) => setPaymentData({...paymentData, expiryDate: formatExpiryDate(text)})}
                    placeholder="MM/YY"
                    keyboardType="numeric"
                    maxLength={5}
                  />
                </View>
                <View style={[styles.inputGroup, styles.halfWidth]}>
                  <Text style={styles.inputLabel}>CVV</Text>
                  <AppTextInput
                    style={styles.input}
                    value={paymentData.cvv}
                    onChangeText={(text) => setPaymentData({...paymentData, cvv: text.replace(/\D/g, '').slice(0, 4)})}
                    placeholder="123"
                    keyboardType="numeric"
                    maxLength={4}
                    secureTextEntry
                  />
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Cardholder Name</Text>
                <AppTextInput
                  style={styles.input}
                  value={paymentData.name}
                  onChangeText={(text) => setPaymentData({...paymentData, name: text})}
                  placeholder="Enter cardholder name"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Email</Text>
                <AppTextInput
                  style={styles.input}
                  value={paymentData.email}
                  onChangeText={(text) => setPaymentData({...paymentData, email: text})}
                  placeholder="john@example.com"
                  keyboardType="email-address"
                />
              </View>

              <TouchableOpacity
                style={styles.payButton}
                onPress={() => handleProcessPayment()}
                disabled={processing}
              >
                <Text style={styles.payButtonText}>
                  Pay ${paymentRequest.amount}
                </Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    padding: 20,
  },
  errorText: {
    fontSize: 18,
    color: '#FF3B30',
    marginBottom: 20,
    textAlign: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  backButton: {
    marginRight: 15,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  paymentDetailsCard: {
    backgroundColor: 'white',
    margin: 20,
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  businessInfo: {
    marginBottom: 20,
  },
  businessName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  businessCategory: {
    fontSize: 14,
    color: '#8E8E93',
  },
  serviceInfo: {
    marginBottom: 20,
  },
  serviceName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  serviceDescription: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
  },
  amountSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 15,
    borderTopWidth: 1,
    borderTopColor: '#E1E5E9',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  amountLabel: {
    fontSize: 16,
    color: '#8E8E93',
  },
  amountValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#34C759',
  },
  expirationInfo: {
    paddingTop: 15,
    alignItems: 'center',
  },
  expirationLabel: {
    fontSize: 14,
    color: '#FF9500',
    fontWeight: '600',
  },
  section: {
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: 'white',
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 15,
  },
  paymentMethodsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  paymentMethodCard: {
    width: '48%',
    padding: 15,
    backgroundColor: '#F8F9FA',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    alignItems: 'center',
    marginBottom: 10,
  },
  selectedPaymentMethod: {
    backgroundColor: '#E3F2FD',
    borderColor: '#007AFF',
  },
  paymentMethodIconWrap: {
    width: 44,
    height: 44,
    marginBottom: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentMethodName: {
    fontSize: 14,
    color: '#1C1C1E',
    textAlign: 'center',
    marginBottom: 4,
  },
  paymentMethodBadge: {
    fontSize: 10,
    color: '#007AFF',
    backgroundColor: '#E3F2FD',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  securitySection: {
    backgroundColor: 'white',
    margin: 20,
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
  },
  securityTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  securityText: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
  },
  processingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  processingCard: {
    backgroundColor: 'white',
    padding: 30,
    borderRadius: 12,
    alignItems: 'center',
    margin: 20,
  },
  processingText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginTop: 15,
    marginBottom: 8,
  },
  processingSubtext: {
    fontSize: 14,
    color: '#8E8E93',
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '90%',
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  modalCloseButton: {
    fontSize: 20,
    color: '#8E8E93',
  },
  paymentForm: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 15,
  },
  halfWidth: {
    width: '48%',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#1C1C1E',
    backgroundColor: 'white',
  },
  payButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 15,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20,
  },
  payButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default CustomerPaymentScreen;

